import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DispositivoInfo } from '@/core/sdk/modules/administracion/sesionesClient';

/**
 * Store de estado de sesión bloqueada por duplicación
 * 
 * Bounded Context: Autenticación
 * Responsabilidad: Gestionar estado persistente de sesión bloqueada por duplicación
 * 
 * SEGURIDAD: Persist middleware asegura que estado persista en localStorage
 * para prevenir bypass por refresh/depuración
 */
interface SesionBloqueadaState {
    /** Si la sesión está bloqueada por duplicación */
    isSesionBloqueada: boolean;

    /** Tipo de bloqueo: 'local' (otra tab) o 'remota' (otro dispositivo) */
    tipoBloqueo: 'local' | 'remota' | null;

    /** Información del dispositivo/tab conflictivo */
    dispositivoConflictivo: DispositivoInfo | null;

    /** Timestamp del momento de bloqueo */
    bloqueadaEn: Date | null;

    /** Bloquear sesión */
    bloquearSesion: (tipo: 'local' | 'remota', dispositivo?: DispositivoInfo) => void;

    /** Desbloquear sesión */
    desbloquearSesion: () => void;

    /** Verificar si esta tab fue cerrada remotamente */
    tabCerradaRemotamente: boolean;

    /** Marcar que esta tab fue cerrada remotamente */
    marcarTabCerrada: () => void;
}

/**
 * Hook useSesionBloqueada - Gestión de sesión bloqueada con persistencia
 * 
 * NOTA SEGURIDAD: Estado se guarda en localStorage para prevenir
 * bypass refrescando página o usando DevTools
 * 
 * Pattern similar a useLock() para bloqueo por inactividad
 * 
 * @example
 * const { isSesionBloqueada, tipoBloqueo, bloquearSesion, desbloquearSesion } = useSesionBloqueada();
 * 
 * // Bloquear por sesión duplicada local
 * bloquearSesion('local');
 * 
 * // Bloquear por sesión duplicada remota
 * bloquearSesion('remota', dispositivoInfo);
 * 
 * // Desbloquear después de cerrar otras sesiones
 * desbloquearSesion();
 */
export const useSesionBloqueada = create<SesionBloqueadaState>()(
    persist(
        (set) => ({
            isSesionBloqueada: false,
            tipoBloqueo: null,
            dispositivoConflictivo: null,
            bloqueadaEn: null,
            tabCerradaRemotamente: false,

            bloquearSesion: (tipo, dispositivo) => set({
                isSesionBloqueada: true,
                tipoBloqueo: tipo,
                dispositivoConflictivo: dispositivo || null,
                bloqueadaEn: new Date(),
            }),

            desbloquearSesion: () => set({
                isSesionBloqueada: false,
                tipoBloqueo: null,
                dispositivoConflictivo: null,
                bloqueadaEn: null,
                tabCerradaRemotamente: false,
            }),

            marcarTabCerrada: () => set({
                tabCerradaRemotamente: true,
                isSesionBloqueada: true,
                tipoBloqueo: 'remota',
            }),
        }),
        {
            name: 'zafiro-sesion-bloqueada-storage', // Key en localStorage
        }
    )
);
